import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Download, Search, Calendar, User, Activity } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_TYPES = [
  'login', 'logout', 'login_failed', 'statement_upload', 'statement_error',
  'transaction_create', 'transaction_update', 'transaction_delete',
  'admin_view_dashboard', 'admin_impersonate', 'admin_toggle_user',
  'admin_delete_user', 'admin_reset_password', 'admin_unlock_user'
];

const ACTION_COLORS = {
  login: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  logout: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
  login_failed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  statement_upload: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  statement_error: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  admin_view_dashboard: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  admin_impersonate: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
};

export default function AuditLogPage() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    action_type: 'all',
    username: '',
    days: 7,
  });

  useEffect(() => {
    loadLogs();
  }, [filters]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/admin/audit-logs', { params: filters });
      setLogs(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  const exportLogs = async () => {
    try {
      const { data } = await api.get('/api/admin/audit-logs', { params: filters });
      const csv = [
        ['Timestamp', 'User', 'Action', 'Target User', 'IP Address', 'Details'].join(','),
        ...data.map(log => [
          log.timestamp,
          log.username || '-',
          log.action_type,
          log.target_username || '-',
          log.ip_address || '-',
          JSON.stringify(log.metadata || {})
        ].join(','))
      ].join('\n');
      
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Logs exported');
    } catch (e) {
      toast.error('Export failed');
    }
  };

  const formatTimestamp = (ts) => {
    const d = new Date(ts);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight font-['Outfit']">Audit Log</h1>
            <p className="text-sm text-muted-foreground mt-1">User activity and admin actions</p>
          </div>
        </div>
        <Button size="sm" onClick={exportLogs}>
          <Download className="h-4 w-4 mr-1" />Export CSV
        </Button>
      </div>

      <Card className="border border-border shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="text-sm font-medium">Activity Logs</CardTitle>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-xs">{logs.length} entries</Badge>
            </div>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-3">
          {/* Filters */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Filter by username..."
                value={filters.username}
                onChange={(e) => setFilters(f => ({ ...f, username: e.target.value }))}
                className="h-8 text-xs pl-8"
              />
            </div>
            <Select value={filters.action_type} onValueChange={(v) => setFilters(f => ({ ...f, action_type: v }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {ACTION_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={String(filters.days)} onValueChange={(v) => setFilters(f => ({ ...f, days: parseInt(v) }))}>
              <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="1">Last 24 hours</SelectItem>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          <div className="rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[140px]">Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead className="w-[120px]">IP Address</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      <TableCell colSpan={5}>
                        <div className="h-6 bg-muted rounded animate-pulse" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground text-sm">
                      No logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map((log) => (
                    <TableRow key={log._id}>
                      <TableCell className="text-xs tabular-nums">
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-medium">{log.username || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-xs ${ACTION_COLORS[log.action_type] || ''}`}>
                          {log.action_type.replace(/_/g, ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.target_username || '-'}
                      </TableCell>
                      <TableCell className="text-xs font-mono text-muted-foreground">
                        {log.ip_address || '-'}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
